import { useState } from "react";
import { isGuestTier } from "@/helpers/guest-tier";
import { useJobRoles, useMyMembership } from "@/hooks/use-job-roles";
import { useStore } from "@/stores/store-context";
import type { TJobRole } from "@/types/job-role";

export type MyJobRole = {
  /** Whether the Job role picker is on screen. The Preferences intro names the
   *  picker only while this is true, so keep the two reading this one value. */
  shown: boolean;
  workspaceSlug: string | undefined;
  jobRoleId: string | null;
  setJobRoleId: (id: string | null) => void;
  /** The workspace's job roles, for the picker's choices. */
  jobRoles: TJobRole[];
};

/**
 * The current member's job role and the workspace's roles, for the workspace in
 * the URL, as cached reads (NG-2802). Shown only inside a workspace, once the
 * membership has loaded, and never for guests, who cannot hold a job role. A
 * membership that failed to load keeps it hidden too: an empty picker would
 * read as "no job role" whatever the member holds, and could not save
 * (NG-2642).
 *
 * `setJobRoleId` shows a pick at once, ahead of its save; the saved
 * membership then answers with the same value.
 *
 * Reads the signed-in user from a store, so the caller must be an `observer`.
 */
export function useMyJobRole(): MyJobRole {
  const { router } = useStore();
  const workspaceSlug = router.workspaceSlug;
  const membership = useMyMembership(workspaceSlug);
  const jobRoles = useJobRoles(workspaceSlug).data;
  // A pick not yet confirmed by the server, or `undefined` to show the saved one.
  const [picked, setPicked] = useState<{ slug: string; id: string | null } | null>(null);

  const role = membership.data?.role ?? null;
  // A Guest cannot hold a job role. By tier, so the built-in Requester is one too
  // (NG-3903): its slug is not the seeded `workspace.guest`.
  const guest = isGuestTier({ role, tier: membership.data?.tier });
  const saved = membership.data?.job_role_id ?? null;
  const jobRoleId = picked && picked.slug === workspaceSlug ? picked.id : saved;

  return {
    shown: Boolean(workspaceSlug) && membership.loaded && role !== null && !guest,
    workspaceSlug,
    jobRoleId,
    setJobRoleId: (id) => setPicked(workspaceSlug ? { slug: workspaceSlug, id } : null),
    jobRoles,
  };
}
